import axios from "axios";
import { useMemo } from "react";
import { useAuth } from "@clerk/react";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export function useApi() {
  const { getToken } = useAuth();
  return useMemo(() => {
    const inst = axios.create({ baseURL: API });
    inst.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      const purpose = sessionStorage.getItem("musora_support_purpose");  // staff only: declared purpose of access, traced server-side
      if (purpose) config.headers["X-Support-Purpose"] = purpose;
      return config;
    });
    inst.interceptors.response.use(undefined, (e) => {  // staff without a declared purpose: ask for it (PurposeGate)
      if (e?.response?.status === 428) window.dispatchEvent(new CustomEvent("musora:purpose-required"));
      return Promise.reject(e);
    });
    return inst;
  }, [getToken]);
}

const FIELD_IT = { sku: "Codice (SKU)", name: "Nome", email: "Email", phone: "Telefono", price_cents: "Prezzo", compare_cents: "Prezzo barrato", stock: "Scorte",
  label: "Etichetta", code: "Codice", value: "Valore", title: "Titolo", slug: "Indirizzo pagina", description: "Descrizione", interval_count: "Ogni quanti periodi",
  trial_days: "Giorni di prova", duration_min: "Durata", amount_cents: "Importo", rate_bp: "Aliquota", country: "Paese", countries: "Paesi" };
const GROUP_IT = { variants: "Variante", lines: "Riga", items: "Articolo", methods: "Spedizione", rates: "Aliquota", staff_ids: "Operatore" };
const TYPE_IT = { missing: "obbligatorio", string_too_short: "obbligatorio", string_too_long: "troppo lungo", greater_than_equal: "valore troppo basso",
  greater_than: "valore troppo basso", less_than_equal: "valore troppo alto", less_than: "valore troppo alto", int_parsing: "deve essere un numero intero",
  float_parsing: "deve essere un numero", too_short: "almeno un elemento", string_pattern_mismatch: "formato non valido", literal_error: "valore non ammesso" };

// One pydantic error → Italian «Variante 2 · Codice (SKU): obbligatorio».
function fieldError(x) {
  const loc = (x.loc || []).filter((p) => p !== "body");
  const parts = [];
  loc.forEach((p, i) => { if (typeof p === "number") parts.push(`${GROUP_IT[loc[i - 1]] || "Elemento"} ${p + 1}`); else if (i === loc.length - 1) parts.push(FIELD_IT[p] || p.replace(/_/g, " ")); });
  const text = x.type === "value_error" ? String(x.msg || "").replace(/^Value error, /, "") : TYPE_IT[x.type] || "valore non valido";
  return parts.length ? `${parts.join(" · ")}: ${text}` : text;
}

export function errMsg(e, fallback = "Si è verificato un errore") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(fieldError).join(" · ");
  return e?.message || fallback;
}

export const assetUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

export const newId = (prefix) => `${prefix}_${crypto.randomUUID().replace(/-/g, "").slice(0, 10)}`;

export const LOCALES = { it: "it-IT", en: "en-GB", es: "es-ES" };

// Display only (the stored ISO timestamp is untouched); lang selects the locale, Italian by default.
export function formatDate(iso, withTime = false, lang = "it") {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(LOCALES[lang] || "it-IT", { day: "numeric", month: "short", year: "numeric", timeZone: "Europe/Rome", ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}) });
}

export function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "pochi secondi fa";
  if (s < 3600) return `${Math.floor(s / 60)} min fa`;
  if (s < 86400) return `${Math.floor(s / 3600)} ore fa`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)} giorni fa`;
  return formatDate(iso);
}

export function formatBytes(n) {
  if (!n) return "0 KB";
  return n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}

export async function downloadFile(api, url, fallbackName) {
  const res = await api.get(url, { responseType: "blob" });
  const m = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "");
  const href = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = href;
  a.download = m ? m[1] : fallbackName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

export const demoUrl = (slug) => `${window.location.origin}/demo/${slug}`;
